import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Lightbulb, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg, useApi } from "@/lib/api";

const DONE = { approved: "Regola attivata: entra nel prompt delle prossime generazioni", rejected: "Regola rifiutata", proposed: "Regola rimessa in proposta" };
const BADGE = {
  approved: ["Attiva nei prompt", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  rejected: ["Rifiutata", "bg-slate-100 text-slate-600 border-slate-200"],
  proposed: ["Proposta: da decidere", "bg-amber-50 text-amber-800 border-amber-200"],
};

export function useVisualRules(dep) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/visual-rules").then(({ data: d }) => setData(d)).catch(() => setData(false)), [api]);
  useEffect(() => { load(); }, [load, dep]);
  const decide = async (cat, status) => {
    setBusy(cat);
    try { const { data: d } = await api.post(`/visual-rules/${cat}/decision`, { status }); setData(d); toast.success(DONE[status]); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  return { data, busy, decide };
}

function Actions({ c, busy, decide }) {
  if (!c.rule || !c.status) return null;
  const b = busy === c.category;
  const btn = (status, label, Icon, cls, testid) => (
    <Button size="sm" variant={status === "approved" ? "default" : "outline"} className={`h-7 text-xs ${cls}`} disabled={b} onClick={() => decide(c.category, status)} data-testid={`${testid}-${c.category}`}>
      {b ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Icon size={12} className="mr-1" />}{label}
    </Button>
  );
  return (
    <div className="flex flex-wrap gap-1.5">
      {c.status !== "approved" && btn("approved", "Approva regola", Check, "bg-emerald-600 hover:bg-emerald-700", "rule-approve")}
      {c.status === "proposed" && btn("rejected", "Rifiuta", X, "", "rule-reject")}
      {c.status === "approved" && btn("rejected", "Disattiva", X, "", "rule-disable")}
    </div>
  );
}

function RuleText({ c }) {
  if (!c.rule) return null;
  return (
    <details className="text-[11px]" data-testid={`rule-text-${c.category}`}>
      <summary className="cursor-pointer text-sky-700">Testo della regola aggiunta al prompt di generazione (in inglese, come lo legge l'AI)</summary>
      <p className="mt-1 rounded-md bg-slate-50 border border-slate-200 p-2 font-mono text-slate-700 leading-relaxed">{c.rule}</p>
    </details>
  );
}

// compact notice inside the review sheet: only rules waiting for a decision
export function RuleProposals({ dep }) {
  const { data, busy, decide } = useVisualRules(dep);
  const list = (data?.categories || []).filter((c) => c.status === "proposed" && c.rule);
  if (!list.length) return null;
  return (
    <div className="space-y-2" data-testid="rule-proposals">
      {list.map((c) => (
        <div key={c.category} className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid={`rule-proposal-${c.category}`}>
          <p className="flex items-start gap-2 text-sm text-amber-950"><Lightbulb size={15} className="mt-0.5 shrink-0" />
            <span>Difetto ricorrente: <b>{c.label}</b> in {c.projects} progetti ({c.defects} {c.defects === 1 ? "caso" : "casi"}). Vuoi aggiungere una regola al prompt di generazione per evitarlo nelle prossime demo?</span></p>
          <RuleText c={c} />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Actions c={c} busy={busy} decide={decide} />
            <a href="/settings#visual-rules" target="_blank" rel="noreferrer" className="text-[11px] text-sky-700 hover:underline" data-testid="rule-proposals-all-link">Tutte le regole apprese</a>
          </div>
        </div>
      ))}
    </div>
  );
}

function RuleRow({ c, threshold, busy, decide }) {
  const [label, cls] = BADGE[c.status] || [`${Math.min(c.projects, threshold)} di ${threshold} progetti`, "bg-white text-slate-600 border-slate-200"];
  return (
    <li className="py-3 space-y-1.5" data-testid={`rule-row-${c.category}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-slate-900">{c.label}</p>
          <span className={`rounded-full border px-2 py-0.5 text-[11px] ${cls}`} data-testid={`rule-status-${c.category}`}>{c.rule ? label : "Senza regola"}</span>
        </div>
        <Actions c={c} busy={busy} decide={decide} />
      </div>
      <p className="text-xs text-slate-500 tabular-nums">{c.projects} {c.projects === 1 ? "progetto" : "progetti"} · {c.defects} {c.defects === 1 ? "difetto" : "difetti"} · {c.fixed} corretti</p>
      {c.effect && <p className="text-xs text-emerald-700" data-testid={`rule-effect-${c.category}`}>Da quando è attiva: {c.effect.reviewed_projects} progetti revisionati, {c.effect.with_defect} con lo stesso difetto.</p>}
      {c.examples?.length > 0 && <ul className="text-[11px] text-slate-500 space-y-0.5">{c.examples.map((e, i) => <li key={i} className="truncate" title={e.evidence}>«{e.evidence}» · {e.project}</li>)}</ul>}
      <RuleText c={c} />
    </li>
  );
}

export default function VisualRules() {
  const { data, busy, decide } = useVisualRules();
  useEffect(() => { if (data && window.location.hash === "#visual-rules") document.getElementById("visual-rules")?.scrollIntoView({ behavior: "smooth" }); }, [!!data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <p className="text-sm text-slate-500">{data === false ? "Statistiche non disponibili" : "Caricamento…"}</p>;
  if (!data.categories.length) return <p className="text-sm text-slate-500" data-testid="rules-empty">Nessun difetto registrato: le statistiche si riempiono con le analisi della revisione visiva.</p>;
  return <ul className="divide-y divide-slate-100" data-testid="rules-list">{data.categories.map((c) => <RuleRow key={c.category} c={c} threshold={data.threshold} busy={busy} decide={decide} />)}</ul>;
}
